import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { 
  Search, 
  Filter, 
  Eye, 
  MapPin, 
  Clock, 
  User, 
  Phone,
  Navigation,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useBookingHistory, useUpdateBookingStatus } from '../../hooks/useBooking';
import { useAuth } from '../../contexts/AuthContext';
import bookingService from '../../services/bookingService';


// Define getDateFilterParams function before it's used
const getDateFilterParams = (filter) => {
  const now = new Date();
  switch (filter) {
    case 'today':
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return {
        date_from: today.toISOString(),
        date_to: new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString()
      };
    case 'week':
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return {
        date_from: weekAgo.toISOString(),
        date_to: now.toISOString()
      };
    case 'month':
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return {
        date_from: monthAgo.toISOString(),
        date_to: now.toISOString()
      };
    default:
      return { date_from: undefined, date_to: undefined };
  }
};

const BookingHistory = () => {
  const { user, isAuthenticated } = useAuth();
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(''); // ADD THIS
  const [currentPage, setCurrentPage] = useState(1);

  // ADD DEBOUNCING EFFECT
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Memoize booking history parameters
  const historyParams = useMemo(() => {
    const params = {
      status: statusFilter === 'all' ? undefined : statusFilter,
      date_from: getDateFilterParams(dateFilter).date_from,
      date_to: getDateFilterParams(dateFilter).date_to,
      ...(debouncedSearchTerm && { search: debouncedSearchTerm }), // CHANGE: Use debouncedSearchTerm
      page: currentPage,
      limit: 10
    };
    
    // Add user-specific context based on user role
    if (user?.hospital?.id) {
      params.hospital_id = user.hospital.id;
    } else if (user?.operator?.id) {
      params.operator_id = user.operator.id;
    }
    
    // Add debugging
    console.log('?? BookingHistory historyParams:', params);
    console.log('?? statusFilter:', statusFilter);
    console.log('?? status param:', params.status);
    console.log('?? BookingHistory user:', user);
    
    return params;
  }, [statusFilter, dateFilter, debouncedSearchTerm, currentPage, user]); // ADD user to dependencies

  // Simplified - backend handles SUPER_ADMIN users automatically
  const { data: historyData, loading, error, refresh, currentPage: apiCurrentPage, totalPages, setCurrentPage: setApiCurrentPage } = useBookingHistory(historyParams);

  const { mutate: updateStatus, loading: updatingStatus } = useUpdateBookingStatus();
  const bookings = historyData || [];

  // Add debugging
  useEffect(() => {
    console.log('BookingHistory Debug Info:');
    console.log('historyData:', historyData);
    console.log('bookings:', bookings);
    console.log('bookings.length:', bookings.length);
    console.log('loading:', loading);
    console.log('error:', error);
  }, [historyData, bookings, loading, error]);

  // Add debugging for filter changes
  useEffect(() => {
    console.log('?? BookingHistory Filter Debug:');
    console.log('statusFilter:', statusFilter);
    console.log('dateFilter:', dateFilter);
    console.log('searchTerm:', searchTerm);
    console.log('currentPage:', currentPage);
    console.log('historyParams:', historyParams);
  }, [statusFilter, dateFilter, searchTerm, currentPage, historyParams]);

  // Sync local page state with API page state
  useEffect(() => {
    if (apiCurrentPage !== currentPage) {
      setCurrentPage(apiCurrentPage);
    }
  }, [apiCurrentPage, currentPage]);

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
  };

  const handleDateFilter = (value) => {
    setDateFilter(value);
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setApiCurrentPage(newPage);
  };

  const handleStatusUpdate = async (bookingId, newStatus, reason) => {
    try {
      // New endpoint: /v1/hospital/bookings/:bookingId/status
      // Response structure: { success: true, message: "...", data: { booking_id: "...", old_status: "...", new_status: "...", updated_at: "..." } }
      const result = await updateStatus(bookingService.updateBookingStatus, bookingId, {
        status: newStatus,
        reason: reason || `Status updated to ${newStatus}`
      });
      
      // Handle response from new API structure
      if (result?.success) {
        console.log('✅ Booking status updated successfully:', {
          bookingId: result.data?.booking_id || bookingId,
          oldStatus: result.data?.old_status,
          newStatus: result.data?.new_status,
          updatedAt: result.data?.updated_at,
          message: result.message
        });
        
        // Refresh the booking list to show updated status
      refresh();
      } else {
        console.warn('⚠️ Status update response missing success flag:', result);
        refresh(); // Still refresh to get latest data
      }
    } catch (error) { 
      console.error('❌ Failed to update booking status:', error);
      // Error is already handled by the hook, but we can add user-facing error handling here if needed
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return 'bg-orange-100 text-orange-800';
      case 'CONFIRMED':
        return 'bg-blue-100 text-blue-800';
      case 'ASSIGNED':
        return 'bg-yellow-100 text-yellow-800';
      case 'IN_PROGRESS':
        return 'bg-purple-100 text-purple-800';
      case 'ARRIVED':
        return 'bg-teal-100 text-teal-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      case 'NO_SHOW':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return <Clock className="w-4 h-4" />;
      case 'CONFIRMED':
        return <CheckCircle className="w-4 h-4" />;
      case 'ASSIGNED':
        return <User className="w-4 h-4" />;
      case 'IN_PROGRESS':
        return <Navigation className="w-4 h-4" />;
      case 'ARRIVED':
        return <MapPin className="w-4 h-4" />;
      case 'COMPLETED':
        return <CheckCircle className="w-4 h-4" />;
      case 'CANCELLED':
        return <XCircle className="w-4 h-4" />;
      case 'NO_SHOW':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getStatusDisplayText = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return 'Pending';
      case 'CONFIRMED':
        return 'Confirmed';
      case 'ASSIGNED':
        return 'Assigned';
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'ARRIVED':
        return 'Arrived';
      case 'COMPLETED':
        return 'Completed';
      case 'CANCELLED':
        return 'Cancelled';
      case 'NO_SHOW':
        return 'No Show';
      default:
        return status || 'Unknown';
    }
  };

  const handleExport = () => {
    if (!bookings || bookings.length === 0) {
      alert('No data to export');
      return;
    }

    // Create CSV headers
    const headers = [
      'Booking ID',
      'Patient Name',
      'Patient Phone',
      'Status',
      'Pickup Address',
      'Destination Address',
      'Created Date',
      'Completed Date'
    ];

    // Create CSV rows
    const csvRows = [
      headers.join(','),
      ...bookings.map(booking => [
        booking.booking_id || '',
        `"${booking.patient_name || ''}"`,
        booking.patient_phone || '',
        booking.status || '',
        `"${booking.pickup_location?.address || ''}"`,
        `"${booking.dropoff_location?.address || ''}"`,
        new Date(booking.created_at).toLocaleDateString(),
        booking.completed_at ? new Date(booking.completed_at).toLocaleDateString() : ''
      ].join(','))
    ];

    // Create and download CSV
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `booking-history-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading booking history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
        <p className="text-lg font-medium text-gray-900 mb-2">Error Loading History</p>
        <p className="text-gray-600 mb-4">
          {error?.message || error?.response?.data?.message || JSON.stringify(error) || 'Failed to load booking history'}
        </p>
        <Button onClick={() => {
          console.log('Refresh button clicked');
          console.log('refresh function:', refresh);
          refresh();
        }} variant="outline" className="bg-blue-600 hover:bg-blue-700">
          <RefreshCw className="w-4 h-4 mr-1" />
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Booking History</h2>
          <p className="text-gray-600">View and manage past ambulance bookings</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleExport} variant="outline" className="text-green-600 hover:text-green-700">
            Export
          </Button>
          <Button onClick={() => {
            console.log('Refresh button clicked');
            console.log('refresh function:', refresh);
            refresh();
          }} variant="outline" className="text-blue-600 hover:text-blue-700" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>


      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by patient name, pickup, or destination..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="!pl-9"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => handleStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="CONFIRMED">Confirmed</option>
                <option value="ASSIGNED">Assigned</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="ARRIVED">Arrived</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="NO_SHOW">No Show</option>
              </select>
            </div>
            <div className="sm:w-48">
              <select
                value={dateFilter}
                onChange={(e) => handleDateFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bookings List */}
      {bookings.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Booking History</h3>
            <p className="text-gray-600">No bookings found matching your criteria.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {bookings.map((booking) => (
            <Card key={booking.booking_id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Badge className={getStatusColor(booking.status)}>
                        {getStatusIcon(booking.status)}
                        <span className="ml-1">{getStatusDisplayText(booking.status)}</span>
                      </Badge>
                      <span className="text-sm text-gray-500">
                        #{booking.booking_id}
                      </span>
                      <span className="text-sm text-gray-500">
                        {new Date(booking.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Patient Information</h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span>{booking.patient_name || 'N/A'}</span>
                          </div>
                          {booking.patient_phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4 text-gray-400" />
                              <span>{booking.patient_phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Trip Details</h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div>
                              <p className="font-medium">Pickup</p>
                              <p className="text-gray-600">{booking.pickup_address || booking.pickup_location?.address || 'N/A'}</p>
                            </div>
                          </div>
                          {(booking.dropoff_address || booking.dropoff_location?.address) && (
                            <div className="flex items-start gap-2">
                              <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                              <div>
                                <p className="font-medium">Destination</p>
                                <p className="text-gray-600">{booking.dropoff_address || booking.dropoff_location?.address || 'N/A'}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>Created: {new Date(booking.created_at).toLocaleString()}</span>
                      </div>
                      {booking.completed_at && (
                        <div className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          <span>Completed: {new Date(booking.completed_at).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 ml-4">
                    <Button
                      onClick={() => handleStatusUpdate(booking.booking_id, 'COMPLETED', 'Trip completed')}
                      size="sm"
                      variant="outline"
                      className="text-green-600 hover:text-green-700"
                      disabled={updatingStatus || booking.status === 'COMPLETED'}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Complete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <Button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  size="sm"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BookingHistory;


